import {useEffect, useMemo, useState} from 'react';

import {Avatar} from 'shells/avatar/avatar';
import {LeadAvatar} from 'shells/avatar/lead-avatar';
import {CompanyRegularIcon} from 'shells/icon/svg/company-regular-icon';
import {PipelineRegularIcon} from 'shells/icon/svg/pipeline-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import {StageCarouselMini} from 'shells/lead/stage-carousel-mini/stage-carousel-mini';
import {LeadValue} from 'shells/lead/value/lead-value';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {LeadPriority} from 'nutshell-graphql-types';
import type {
    InboxThreadFragment,
    InboxThreadFragment_relatedLeads as RelatedLead,
} from 'nutshell-graphql-types';

import {LeadSidebarEmptyState} from './lead-sidebar-empty-state';
import {LeadSidebarRowItem} from './lead-sidebar-row-item';

interface Props {
    isLoadingRelatedLeads: boolean;
    thread: InboxThreadFragment;
}

export function LeadSidebarContent(props: Props) {
    const relatedLeads = useMemo(
        () => props.thread.relatedLeads ?? [],
        [props.thread.relatedLeads]
    );
    const [currentLead, setCurrentLead] = useState<RelatedLead | undefined>(relatedLeads[0]);

    useEffect(() => {
        setCurrentLead((prev) => {
            return relatedLeads.find((lead) => lead.id === prev?.id) ?? relatedLeads[0];
        });
    }, [relatedLeads]);

    if (props.isLoadingRelatedLeads) {
        return <LoadingPage size={50} padding={16} />;
    }

    if (!currentLead) {
        return <LeadSidebarEmptyState thread={props.thread} />;
    }

    const assignee = currentLead.assignee;
    const assigneeIsUser = assignee?.__typename === 'User';

    const company = currentLead.accounts.primaryEdge?.node;
    const stages = currentLead.stages.edges.map((edge) => ({
        id: edge.node.id,
        name: edge.node.name,
        isActive: edge.isActive,
    }));

    return (
        <div className='flex flex-dir-col pad-16 gap-8'>
            <Stack isHorizontal={true} spacing={8} alignItems='center'>
                <LeadAvatar avatarUrl={currentLead.avatarUrl} size={24} />
                <Link
                    href={currentLead.htmlUrl}
                    size='normal'
                    isBold={true}
                    shouldTruncate={true}
                    newTab={true}
                >
                    {currentLead.name}
                </Link>
            </Stack>
            {currentLead.value && (
                <LeadValue valueFormatted={currentLead.value.formatted} variant='mini' />
            )}
            <Stack spacing={8}>
                <LeadSidebarRowItem
                    icon={
                        <Avatar
                            avatarUrl={assigneeIsUser ? assignee.avatarUrl : undefined}
                            initials={assigneeIsUser ? assignee.initials : undefined}
                            size={16}
                            type={assigneeIsUser ? 'users' : 'teams'}
                        />
                    }
                    value={currentLead.assignee?.name}
                    fallbackText='Unassigned'
                />
                {company && (
                    <LeadSidebarRowItem
                        icon={<CompanyRegularIcon size={16} wrapWithDiv={false} />}
                        value={company.name}
                    />
                )}
                <LeadSidebarRowItem
                    icon={<PipelineRegularIcon size={16} wrapWithDiv={false} />}
                    value={currentLead.stageset?.name}
                    fallbackText='No pipeline'
                />
            </Stack>
            {currentLead.stageset && (
                <StageCarouselMini
                    stages={stages}
                    isHot={currentLead.priority === LeadPriority.HOT}
                    leadStatus={currentLead.status}
                />
            )}
        </div>
    );
}
